/**
 * Preiszeilen und Katalog-Hilfen für die Anzeige.
 * Der angezeigte Konfigurationspreis kommt ausschließlich
 * von `/calculate-price`.
 */

import { Dimensions } from '../types';
import { calculateFixedPrice } from '../config/priceConfigFixed';
import {
  calculateComfortPrice,
  comfortPricesFromCatalog,
  MotorRules,
} from '../config/priceConfigComfort';
import { calculateLinearPrice } from '../config/priceConfigLinear';
import { calculateCircularPrice } from '../config/priceConfigCircular';
import { calculateWalkOnPrice } from '../config/priceConfigWalkOn';
import { calculateCircularWalkOnPrice } from '../config/priceConfigCircularWalkOn';
import { calculateAccessPrice } from '../config/priceConfigAccess';
import { calculateLinearWalkOnPrice } from '../config/priceConfigLinearWalkOn';
import {
  ACCESSORY_NAMES,
  AccessoryId,
  formatBreakdownItemName,
  formatDimensions,
  formatGlassType,
  roundDiameterToStep,
} from '../utils/summaryHelpers';
import { priceService, productCodeFromName } from './priceService';

export type PriceLine = {
  id: string;
  label: string;
  detail?: string;
  /** Netto in EUR. `null` = im übergeordneten Preis enthalten. */
  net: number | null;
};

/** Rohform aus `/accessories` bzw. `/products/{code}/accessories`. */
export type AccessoryCatalogItem = {
  id?: string | number;
  accessory_code?: string;
  name?: string;
  price_type?: 'fixed' | 'per_sqm' | 'percentage' | 'conditional' | 'per_unit' | string;
  base_price?: number;
  min_quantity?: number;
  glass_type_restriction?: 'double' | 'triple' | 'both' | string;
  category?: string;
  category_name?: string;
  is_required?: boolean | number | string;
  is_default?: boolean | number | string;
};

function catalogCode(item: AccessoryCatalogItem): string {
  return String(item.accessory_code || item.id || '');
}

function isCatalogFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function accessoryAllowedForGlass(
  item: AccessoryCatalogItem,
  glassType?: string | null,
): boolean {
  const restriction = item.glass_type_restriction || 'both';
  if (restriction === 'both' || !glassType) return true;
  return restriction === glassType;
}

type ConfigSlice = {
  product: string;
  dimensions: Dimensions;
  glassType?: string | null;
  accessories?: string[];
  ralColor?: string;
  ralColorInside?: string;
  controlType?: 'wcc103' | 'wcc106' | null;
  modules?: { fixed: number; opening: number };
  catalog?: AccessoryCatalogItem[];
  motorRules?: MotorRules;
};

/** Comfort-Steuerung und Motor werden als eigene Zeilen geführt. */
const COMFORT_INCLUDED_CODES = new Set([
  'wcc103',
  'wcc106',
  'motor_single',
  'motor_dual',
]);

/** RAL läuft über ralColor / ralColorInside, nicht über den Zubehör-Katalog. */
const RAL_ACCESSORY_CODES = new Set([
  'ral',
  'ral_outside_standard',
  'ral_outside_custom',
  'ral_inside_standard',
  'ral_inside_custom',
]);

/**
 * Basispreis für ein Produkt in EUR (netto), ohne Zubehör, ohne
 * RAL-Farb-Aufpreise, ohne Rabatt.
 */
export function calculateBasePrice(
  product: string,
  dimensions: Dimensions,
  glassType?: string | null,
  controlType?: 'wcc103' | 'wcc106' | null,
  catalog?: AccessoryCatalogItem[],
  motorRules?: MotorRules,
): number {
  const glass = glassType || 'double';

  if (product === 'SkyVision FIXED') {
    return calculateFixedPrice(
      dimensions.width,
      dimensions.length,
      dimensions.height,
      glass
    );
  }
  if (product === 'SkyVision COMFORT') {
    const result = calculateComfortPrice(
      dimensions.width,
      dimensions.length,
      dimensions.height,
      glass,
      controlType,
      comfortPricesFromCatalog(resolveAccessoryCatalog(product, catalog)),
      motorRules,
    );
    return result.totalPrice;
  }
  if (product === 'SkyVision ACCESS') {
    const result = calculateAccessPrice(
      dimensions.width,
      dimensions.length,
      dimensions.height,
      glass,
      controlType ?? 'wcc106',
      comfortPricesFromCatalog(resolveAccessoryCatalog(product, catalog)),
    );
    return result.totalPrice;
  }
  if (product === 'SkyVision LINEAR WALK-ON') {
    return calculateLinearWalkOnPrice(dimensions.width, dimensions.length, glass);
  }
  if (product === 'SkyVision LINEAR') {
    const area = (dimensions.width * dimensions.length) / 1000000;
    return calculateLinearPrice(area, glass);
  }
  if (product === 'SkyVision CIRCULAR WALK-ON') {
    const inputDiameter = dimensions.diameter || 0;
    const roundedDiameter = roundDiameterToStep(inputDiameter);
    return calculateCircularWalkOnPrice(roundedDiameter, glass).totalPrice;
  }
  if (product === 'SkyVision CIRCULAR') {
    const inputDiameter = dimensions.diameter || 0;
    const roundedDiameter = roundDiameterToStep(inputDiameter);
    return calculateCircularPrice(roundedDiameter, glass);
  }
  if (product === 'SkyVision WALKON') {
    return calculateWalkOnPrice(dimensions.width, dimensions.length, glass).totalPrice;
  }
  return 0;
}

/** Glasfläche inkl. 260 mm Überstand — gleiche Formel wie im PHP-Backend. */
export function glassAreaSqm(product: string, dimensions: Dimensions): number {
  const circular =
    /circular/i.test(product) ||
    ((dimensions.diameter || 0) > 0 && !(dimensions.width > 0 && dimensions.length > 0));

  if (circular) {
    const inputDiameter = dimensions.diameter || 600;
    const roundedDiameter = roundDiameterToStep(inputDiameter);
    const glassDiameter = (roundedDiameter + 260) / 1000;
    const radius = glassDiameter / 2;
    return Math.PI * radius * radius;
  }

  const width = dimensions.width || 1000;
  const length = dimensions.length || 1000;
  return ((width + 260) / 1000) * ((length + 260) / 1000);
}

export function matchAccessory(
  catalog: AccessoryCatalogItem[] | undefined,
  code: string,
): AccessoryCatalogItem | undefined {
  if (!catalog || !code) return undefined;
  return catalog.find((item) => {
    const accessoryCode = String(item.accessory_code || '');
    const id = String(item.id ?? '');
    return accessoryCode === code || id === code;
  });
}

/**
 * Übernimmt Pflicht- und Standard-Zubehör aus dem Katalog.
 * Motor, Steuerung und RAL bleiben Sonderfälle. Jeder andere
 * Katalogartikel — auch neu angelegter — erscheint und wird bepreist.
 */
export function applyCatalogSelection(
  current: string[],
  catalog: AccessoryCatalogItem[],
  glassType?: string | null,
  applyDefaults = false,
): string[] {
  const system = new Set<string>([
    ...COMFORT_INCLUDED_CODES,
    ...RAL_ACCESSORY_CODES,
  ]);
  const selected = new Set(
    (current || []).filter((code) => {
      if (!code) return false;
      if (system.has(code)) return true;
      const item = matchAccessory(catalog, code);
      if (!item) return true;
      return accessoryAllowedForGlass(item, glassType);
    }),
  );

  for (const item of catalog) {
    const code = catalogCode(item);
    if (!code || system.has(code)) continue;
    if (!accessoryAllowedForGlass(item, glassType)) {
      selected.delete(code);
      continue;
    }
    if (
      isCatalogFlag(item.is_required) ||
      (applyDefaults && isCatalogFlag(item.is_default))
    ) {
      selected.add(code);
    }
  }

  return Array.from(selected);
}

function resolveAccessoryCatalog(
  product: string,
  catalog?: AccessoryCatalogItem[],
): AccessoryCatalogItem[] | undefined {
  if (catalog && catalog.length > 0) return catalog;
  return priceService.peekAccessories(productCodeFromName(product)) || undefined;
}

/** Katalogpreis eines Zubehörteils (fixed / m² / Prozent / Stück). */
export function calculateCatalogAccessoryPrice(
  item: AccessoryCatalogItem,
  product: string,
  dimensions: Dimensions,
  basePrice = 0,
): number {
  const type = item.price_type || 'fixed';
  const unit = Number(item.base_price) || 0;
  const quantity = item.min_quantity || 1;

  switch (type) {
    case 'per_sqm': {
      const area = glassAreaSqm(product, dimensions);
      return area < 1 ? unit : unit * area;
    }
    case 'percentage':
      return basePrice * (unit / 100);
    case 'per_unit':
      return unit * quantity;
    case 'fixed':
    case 'conditional':
    default:
      return unit;
  }
}

export function catalogRalSurcharge(
  catalog: AccessoryCatalogItem[] | undefined,
  location: 'outside' | 'inside',
): number {
  const code =
    location === 'inside' ? 'ral_inside_custom' : 'ral_outside_custom';
  const item = matchAccessory(catalog, code);
  if (item?.base_price == null) return 0;
  const value = Number(item.base_price);
  return Number.isFinite(value) ? value : 0;
}

export function calculateDynamicAccessoryPrice(
  accessoryId: string,
  product: string,
  dimensions: Dimensions
): number {
  const pricePerSqm: Record<string, number> = {
    sun_protection_glass: 135,
    opal_lamination: 95,
    fire_protection_glass: 150,
    sun_protection: 100,
    interior_blackout: 120,
    interior_sun_protection: 90,
  };
  const price = pricePerSqm[accessoryId] || 0;
  if (price === 0) return 0;

  const area = glassAreaSqm(product, dimensions);
  return area < 1 ? price : price * area;
}

export function pickCompletePriceLines(
  local: PriceLine[],
  api: PriceLine[],
): PriceLine[] {
  return presentSummaryPriceLines(local, api);
}

export function sumPriceLines(lines: PriceLine[]): number {
  return lines.reduce((sum, line) => sum + (line.net ?? 0), 0);
}

function prettyBreakdownLabel(item: string, product = ''): string {
  return formatBreakdownItemName(item, product);
}

function splitBreakdownLabel(
  item: string,
  product = '',
): { label: string; detail?: string } {
  const pretty = prettyBreakdownLabel(item || '', product);
  const parts = pretty.split(/\s·\s/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { label: parts[0], detail: parts.slice(1).join(' · ') };
  }
  return { label: pretty || 'Position' };
}

export function mapApiBreakdownToPriceLines(
  breakdown: { item?: string; price?: number }[],
  product = '',
): PriceLine[] {
  return breakdown.map((row, index) => {
    const { label, detail } = splitBreakdownLabel(
      row.item || `Position ${index + 1}`,
      product,
    );
    return {
      id: `api-${index}`,
      label,
      detail,
      net: typeof row.price === 'number' ? row.price : null,
    };
  });
}

function normalizeLineText(value: string): string {
  return value
    .toLowerCase()
    .replace(/skyvision\s*/g, '')
    .replace(/sonderfarbe/g, 'farbe')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/verglasung|isolier|laminierung/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isProductLike(line: PriceLine): boolean {
  if (line.id === 'product') return true;
  return /fixed|comfort|circular|linear|walkon|access|grundpreis/.test(
    normalizeLineText(line.label),
  );
}

function linesReferToSameItem(local: PriceLine, api: PriceLine): boolean {
  if (local.id === 'product') return isProductLike(api);
  if (local.id === 'glass') return false;
  if (local.id === 'ral-out') {
    const hay = normalizeLineText(`${api.label} ${api.detail || ''}`);
    return hay.includes('aussen') && (hay.includes('farbe') || hay.includes('ral'));
  }
  if (local.id === 'ral-in') {
    const hay = normalizeLineText(`${api.label} ${api.detail || ''}`);
    return hay.includes('innen') && (hay.includes('farbe') || hay.includes('ral'));
  }

  const localText = normalizeLineText(`${local.label} ${local.id}`);
  const apiText = normalizeLineText(`${api.label} ${api.detail || ''}`);
  if (!localText || !apiText) return false;
  if (localText === apiText) return true;
  if (apiText.includes(localText) || localText.includes(apiText)) return true;

  const localTokens = localText.split(' ').filter((token) => token.length > 3);
  return localTokens.some(
    (token) => apiText.includes(token) || token.includes(apiText),
  );
}

/**
 * Positionsblatt: immer die gewählte Konfiguration (lokal), Beträge vom
 * Backend wo die Zeilen zusammenpassen. Fehlt Zubehör oder RAL in der API,
 * bleibt die lokale Zeile stehen — sonst verschwinden Optionen.
 */
export function presentSummaryPriceLines(
  local: PriceLine[],
  api: PriceLine[],
): PriceLine[] {
  const apiHasPrices = api.some((line) => (line.net ?? 0) > 0);
  if (!apiHasPrices) return local;

  const used = new Set<number>();
  const merged = local.map((line) => {
    const matchIndex = api.findIndex(
      (candidate, index) =>
        !used.has(index) && linesReferToSameItem(line, candidate),
    );
    if (matchIndex < 0) return line;
    used.add(matchIndex);
    const matched = api[matchIndex];
    if (matched.net == null || matched.net <= 0) return line;
    return { ...line, net: matched.net };
  });

  api.forEach((line, index) => {
    if (used.has(index)) return;
    if ((line.net ?? 0) <= 0) return;
    if (isProductLike(line)) return;
    merged.push(line);
  });

  return merged;
}

function accessoryLabel(id: string): string {
  if (id === 'wcc103') return 'Steuerung WCC103';
  if (id === 'wcc106') return 'Steuerung WCC106';
  if (id === 'motor_single') return 'Motor';
  if (id === 'motor_dual') return 'Zwei Motoren';
  return ACCESSORY_NAMES[id as AccessoryId] || id;
}

/**
 * Einzelpositionen der aktuellen Konfiguration (netto).
 * Verglasung und Standard-RAL stecken im Fensterpreis.
 * Comfort-Motor/Steuerung und gewähltes Zubehör stehen als eigene Zeilen.
 */
export function buildLocalPriceLines(input: ConfigSlice): PriceLine[] {
  const glass = (input.glassType as 'double' | 'triple') || 'double';
  const catalog = resolveAccessoryCatalog(input.product, input.catalog);
  const comfortPrices = comfortPricesFromCatalog(catalog);
  let productNet = calculateBasePrice(
    input.product,
    input.dimensions,
    glass,
    input.controlType,
    catalog,
    input.motorRules,
  );
  const comfortLines: PriceLine[] = [];

  if (input.product === 'SkyVision COMFORT' || input.product === 'SkyVision ACCESS') {
    const result =
      input.product === 'SkyVision ACCESS'
        ? calculateAccessPrice(
            input.dimensions.width,
            input.dimensions.length,
            input.dimensions.height,
            glass,
            input.controlType ?? 'wcc106',
            comfortPrices,
          )
        : calculateComfortPrice(
            input.dimensions.width,
            input.dimensions.length,
            input.dimensions.height,
            glass,
            input.controlType,
            comfortPrices,
            input.motorRules,
          );
    productNet = result.totalPrice - result.motorPrice - result.controlPrice;
    const motorId = result.motorCount === 2 ? 'motor_dual' : 'motor_single';
    comfortLines.push({
      id: motorId,
      label: accessoryLabel(motorId),
      net: result.motorPrice > 0 ? result.motorPrice : null,
    });
    if (input.controlType) {
      comfortLines.push({
        id: input.controlType,
        label: accessoryLabel(input.controlType),
        net: result.controlPrice > 0 ? result.controlPrice : null,
      });
    }
  }

  const lines: PriceLine[] = [
    {
      id: 'product',
      label: input.product || 'Fenster',
      detail: formatDimensions(input.product, input.dimensions, input.modules),
      net: productNet,
    },
    {
      id: 'glass',
      label: formatGlassType(input.glassType),
      net: null,
    },
    ...comfortLines,
  ];

  for (const id of input.accessories || []) {
    if (COMFORT_INCLUDED_CODES.has(id) || RAL_ACCESSORY_CODES.has(id)) {
      continue;
    }
    const catalogItem = matchAccessory(catalog, id);
    if (!catalogItem) continue;
    const price = calculateCatalogAccessoryPrice(
      catalogItem,
      input.product,
      input.dimensions,
      productNet,
    );
    lines.push({
      id,
      label: catalogItem?.name || accessoryLabel(id),
      net: price > 0 ? price : null,
    });
  }

  const ralOut = input.ralColor || 'RAL 7043';
  const ralOutPrice = catalogRalSurcharge(catalog, 'outside');
  lines.push({
    id: 'ral-out',
    label: `Farbe außen ${ralOut}`,
    net: ralOut !== 'RAL 7043' ? ralOutPrice : null,
  });

  if (input.product !== 'SkyVision CIRCULAR') {
    const ralIn = input.ralColorInside || 'RAL 9010';
    const ralInPrice = catalogRalSurcharge(catalog, 'inside');
    lines.push({
      id: 'ral-in',
      label: `Farbe innen ${ralIn}`,
      net: ralIn !== 'RAL 9010' ? ralInPrice : null,
    });
  }

  return lines;
}

/** Laufender Nettopreis: Fenster + Zubehör + RAL-Aufpreise. */
export function computeConfigurationNet(input: ConfigSlice): number {
  return buildLocalPriceLines(input).reduce(
    (sum, line) => sum + (line.net ?? 0),
    0,
  );
}
